import React from "react";
import { Drawer, Box, Typography, Button } from "@mui/material";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";
import { useAppConfig } from "../../../context/AppConfigContext";
import { useCakeAttributes } from "../../../hooks/useCakeAttributes"; // custom hook from refactor
import { useCakeFormState } from "../../../hooks/useCakeFormState"; // custom hook from refactor
import api from "../../../services/api";
import { uploadSpecialtyCakeImage } from "../../../utils/uploadToS3";

// 🧩 Subcomponents
import CakeBasicInfoForm from "./CakeBasicInfoForm";
import CakeSizeFlavorSelector from "./CakeSizeFlavorSelector";
import EventTypeSelector from "./EventTypeSelector";
import AudienceSelector from "./AudienceSelector";
import CakeAttributesSelector from "./CakeAttributesSelector";
import CakeKeywordsFeatured from "./CakeKeywordsFeatured";
import CakeSeoFields from "./CakeSeoFields";
import CakeImageUploader from "./CakeImageUploader";
import CakeDisclaimerSelector from "./CakeDisclaimerSelector";

const SpecialtyCakeFormDrawer = ({ open, onClose, onSave, cakeData }) => {
  const isEditMode = Boolean(cakeData);
  const snackbar = useSnackbarAlert();
  const { config } = useAppConfig();

  const {
    attributeOptions,
    disclaimerOptions,
    loading: attrLoading,
  } = useCakeAttributes();

  const {
    formData,
    setFormData,
    handleChange,
    handleTagsChange,
    handleAttributeChange,
    handleDisclaimerChange,
    handleFileChange,
    uploading,
    loading,
    setLoading,
    resetForm,
  } = useCakeFormState(cakeData, attributeOptions);

  const handleSubmit = async () => {
    setLoading(true);
    const payload = { ...formData };

    if (formData.imageFile) {
      try {
        const fileUrl = await uploadSpecialtyCakeImage(formData.imageFile);
        payload.images.primary = fileUrl;
        delete payload.imageFile;
      } catch (err) {
        console.error("Image upload failed:", err);
        snackbar.error("Image upload failed.");
        setLoading(false);
        return;
      }
    }

    payload.keywords = payload.keywords.split(",").map((kw) => kw.trim());

    try {
      if (isEditMode) {
        await api.put(`/specialty-cakes/${cakeData._id}`, payload);
        snackbar.success("Cake updated successfully.");
      } else {
        await api.post("/specialty-cakes", payload);
        snackbar.success("Cake added successfully.");
        resetForm();
      }
      onSave();
      onClose();
    } catch (err) {
      console.error("Error saving cake:", err);
      snackbar.error("Something went wrong while saving the cake.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      PaperProps={{ sx: { width: { xs: "100%", md: 500 } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" mb={3} fontWeight={700}>
          {isEditMode ? "Edit Cake" : "Add New Cake"}
        </Typography>

        <CakeBasicInfoForm formData={formData} onChange={handleChange} />

        <CakeSizeFlavorSelector
          selectedSizeIds={formData.sizes}
          selectedFlavorIds={formData.flavors}
          onChange={(updated) =>
            setFormData((prev) => ({
              ...prev,
              ...updated,
            }))
          }
        />

        <EventTypeSelector
          eventTypes={config?.eventTypes || []}
          value={formData.eventType}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              eventType: e.target.value,
            }))
          }
        />

        <AudienceSelector
          value={formData.audience}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              audience: e.target.value,
            }))
          }
        />

        <CakeAttributesSelector
          attributeOptions={attributeOptions}
          value={formData.attributes}
          onChange={handleAttributeChange}
        />

        <CakeDisclaimerSelector
          selectedDisclaimerIds={formData.disclaimers}
          onChange={handleDisclaimerChange}
        />

        <CakeKeywordsFeatured formData={formData} onChange={handleChange} />

        <CakeSeoFields
          seo={formData.seo}
          onChange={(seo) => setFormData((prev) => ({ ...prev, seo }))}
        />

        <CakeImageUploader
          uploading={uploading}
          file={formData.imageFile}
          url={formData.images.primary}
          onFileChange={handleFileChange}
        />

        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          sx={{ mt: 4 }}
          disabled={loading}>
          {loading ? "Saving..." : isEditMode ? "Save Changes" : "Add Cake"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default SpecialtyCakeFormDrawer;
